/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useRef } from "react";

import {
  callHistory,
  maxCallHistory,
  type CallHistoryEntry,
} from "../MeetSettings";

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

export function addCallHistory(entry: CallHistoryEntry): void {
  const list = callHistory.getValue();
  const next = [entry, ...list].slice(0, Math.max(1, maxCallHistory.getValue()));
  callHistory.setValue(next);
}

export function clearCallHistory(): void {
  callHistory.setValue([]);
}

export function removeCallHistoryEntry(id: string): void {
  callHistory.setValue(callHistory.getValue().filter((e) => e.id !== id));
}

/**
 * Records a local call-history entry while the call view is mounted.
 * Entries shorter than 1 second (e.g. a failed join) are discarded.
 */
export function useCallHistoryRecording(
  roomName: string,
  getParticipantCount: () => number,
): void {
  const startRef = useRef<number>(Date.now());
  const idRef = useRef<string>(makeId());

  useEffect(() => {
    startRef.current = Date.now();
    idRef.current = makeId();
    return () => {
      const startedAt = startRef.current;
      const durationMs = Date.now() - startedAt;
      if (durationMs < 1000) return;
      addCallHistory({
        id: idRef.current,
        roomName,
        startedAt,
        durationMs,
        participants: getParticipantCount(),
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);
}
